import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Heart, X } from "lucide-react";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Wishlist() {
  const { items, count, refresh, remove } = useWishlist();
  const cart = useCart();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const moveToCart = async (product) => {
    try {
      const { data } = await api.get(`/products/${product.slug}`);
      const sku = data.variants?.[0]?.sku;
      if (!sku) {
        toast.error("No variant available.");
        return;
      }
      await cart.addItem(product.id, sku, 1, product.name);
      await remove(product.id);
    } catch (e) {
      toast.error("Could not move to cart.");
    }
  };

  if (!localStorage.getItem("jow_token")) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="wishlist-locked">
        <span className="label-overline text-jow-gold">Sign in</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy">
          Your wishlist lives behind the velvet rope.
        </h1>
        <p className="mt-3 text-jow-navy/60">
          Sign in to save the pieces you love.
        </p>
        <Link to="/login" className="btn-primary-jow mt-10 inline-flex" data-testid="wishlist-login-cta">
          Sign In
        </Link>
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="wishlist-empty">
        <span className="label-overline text-jow-gold">Empty</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy">
          You haven&rsquo;t saved any pieces yet.
        </h1>
        <Link to="/shop" className="btn-primary-jow mt-10 inline-flex">
          Discover the atelier
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="wishlist-page">
      <div className="mb-10">
        <span className="label-overline text-jow-gold">Your wishlist</span>
        <h1 className="font-display text-4xl md:text-5xl mt-2 text-jow-navy">
          {count} saved {count === 1 ? "piece" : "pieces"}.
        </h1>
      </div>

      <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="wishlist-grid">
        {items.map((p) => (
          <li
            key={p.id}
            className="bg-white border border-jow-navy/10 group"
            data-testid={`wishlist-item-${p.slug}`}
          >
            <Link to={`/product/${p.slug}`} className="block aspect-[4/5] overflow-hidden">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </Link>
            <div className="p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Link to={`/product/${p.slug}`}>
                    <h3 className="font-display text-base md:text-lg text-jow-navy leading-tight">
                      {p.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-jow-navy/85 mt-1">
                    ₹{(p.price_inr || 0).toLocaleString("en-IN")}
                  </p>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  className="text-jow-navy/40 hover:text-jow-pink p-1"
                  data-testid={`wishlist-remove-${p.slug}`}
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              </div>
              <button
                onClick={() => moveToCart(p)}
                className="btn-secondary-jow text-[10px] py-2"
                data-testid={`wishlist-move-${p.slug}`}
              >
                <Heart size={11} fill="#E91E63" stroke="#E91E63" /> Move to cart
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
